/**
 * Redux Reducer for Data Categorization
 *
 * Manages the state for data categorization including:
 * - Categories list
 * - Rules management
 * - Batch processing
 * - Data items
 * - Loading states
 * - Error messages
 * - Success messages
 * - Pagination
 * - Filters
 *
 * @author Development Team
 * @version 2.0
 */

import * as actionTypes from './actionTypes';

const TEAM_REGISTRY_KEY = 'eiot_team_registry';

const loadTeamRegistry = () => {
  try { return JSON.parse(localStorage.getItem(TEAM_REGISTRY_KEY) || '{}'); }
  catch { return {}; }
};

const saveTeamRegistry = (registry) => {
  try { localStorage.setItem(TEAM_REGISTRY_KEY, JSON.stringify(registry)); } catch {}
};

/**
 * Transform raw batch result from API to UI format
 * @param {Object} r - Raw result from API
 * @returns {Object} Transformed result
 */
const transformBatchResult = (r) => {
  let parsedData = {};
  try {
    parsedData = r.originalData ? JSON.parse(r.originalData) : {};
  } catch (e) {
    // Invalid JSON - keep empty object
    console.warn('Failed to parse originalData:', e);
  }

  let uiStatus;
  if (r.isValidated) {
    uiStatus = 'VALIDATED';
  } else if (r.status === 'FAILED') {
    uiStatus = 'FAILED';
  } else if (r.status === 'SUCCESS') {
    uiStatus = 'CATEGORIZED';
  } else {
    uiStatus = 'PENDING';
  }

  return {
    ...r,
    id: r.resultId,
    configurationItem: parsedData['Configuration item'] || parsedData['configuration_item'] || parsedData['configurationItem'] || '',
    description: parsedData['description'] || parsedData['Description'] || '',
    suggestedCategory: r.matchedCategory || null,
    validatedCategory: r.validatedBy ? r.matchedCategory : null,
    confidence: r.confidenceScore !== null && r.confidenceScore !== undefined ? r.confidenceScore / 100 : null,
    status: uiStatus,
    rejectionReason: r.errorMessage || null,
    originalFields: parsedData,
  };
};

/**
 * Build verdict map from bulk attestation requests
 * @param {Array} requests - Array of attestation requests
 * @returns {Object} Map of resultId to verdict
 */
const buildVerdictMap = (requests) => {
  const verdictMap = {};
  requests.forEach(req => {
    verdictMap[req.resultId] = req.verdict || 'ACCEPTED';
  });
  return verdictMap;
};

/**
 * Handle new team registry after adding a team
 * @param {Object} currentRegistry - Current team registry
 * @param {Object} newTeam - Newly added team
 * @returns {Object} Updated registry
 */
const handleNewTeamRegistry = (currentRegistry, newTeam) => {
  if (!newTeam.createdBy) {
    return currentRegistry;
  }
  
  const newRegistry = {
    ...currentRegistry,
    [newTeam.value]: {
      createdBy: newTeam.createdBy,
      members: [],
    },
  };
  saveTeamRegistry(newRegistry);
  return newRegistry;
};

/**
 * Update batch result with attestation
 * @param {Object} result - Batch result to update
 * @param {Object} attested - Attestation payload
 * @returns {Object} Updated result or original if no match
 */
const updateResultWithAttestation = (result, attested) => {
  if (result.resultId === attested.resultId || result.id === attested.resultId) {
    return {
      ...result,
      status: attested.verdict === 'ACCEPTED' ? 'VALIDATED' : 'REJECTED',
      attestation: attested
    };
  }
  return result;
};

/**
 * Remove team from registry
 * @param {Object} registry - Current team registry
 * @param {string} teamValue - Team value to remove
 * @returns {Object} Updated registry without the team
 */
const removeTeamFromRegistry = (registry, teamValue) => {
  // Destructure to remove the team, ignore the removed value to avoid unused variable warning
  // eslint-disable-next-line no-unused-vars
  const { [teamValue]: _, ...remainingRegistry } = registry || {};
  saveTeamRegistry(remainingRegistry);
  return remainingRegistry;
};

/**
 * Normalize rules from API response
 * @param {Array} rawRules - Raw rules from API
 * @returns {Array} Normalized rules
 */
const normalizeRules = (rawRules) => {
  return rawRules.map(r => ({
    ...r,
    ruleId: r.ruleId || r.id,
    name: r.name || r.ruleName,
  }));
};

/**
 * Apply bulk attestation to results
 * @param {Array} results - Current batch results
 * @param {Set} attestedIds - Set of attested result IDs
 * @param {Object} verdictMap - Map of result ID to verdict
 * @returns {Array} Updated results
 */
const applyBulkAttestation = (results, attestedIds, verdictMap) => {
  return results.map(r => {
    const rid = r.resultId || r.id;
    if (attestedIds.has(Number(rid))) {
      const verdict = verdictMap[rid] || 'ACCEPTED';
      return {
        ...r,
        status: 'VALIDATED',
        attestation: { verdict },
      };
    }
    return r;
  });
};

/**
 * Handle adding a new team to state
 * @param {Object} state - Current state
 * @param {Object} newTeam - Team to add
 * @returns {Object} New state after adding team
 */
const handleAddTeamSuccess = (state, newTeam) => {
  const teamExists = state.teams.some(t => t.value === newTeam.value);
  if (teamExists) {
    return {
      ...state,
      teamsLoading: false,
      error: 'Team already exists'
    };
  }
  
  const newRegistry = handleNewTeamRegistry(state.teamRegistry, newTeam);
  return {
    ...state,
    teams: [...state.teams, newTeam],
    teamRegistry: newRegistry,
    teamsLoading: false,
    successMessage: `Team "${newTeam.label}" added successfully`,
    error: null
  };
};

const initialState = {
  // Teams state - starts empty, will be populated from API
  teams: [],
  teamsLoading: false,
  // Registry of team membership (createdBy + members), persisted to localStorage
  teamRegistry: loadTeamRegistry(),
  // Detailed team objects from /teams/all (includes members, roles, etc.)
  teamDetails: [],
  teamDetailsLoading: false,
  // Teams the current user belongs to
  myTeams: [],
  myTeamsLoading: false,
  // Team access check result
  teamAccessResult: null,

  // Categories state
  categories: [],
  selectedCategory: null,

  // Rules state - populated from API
  rules: [],
  selectedRule: null,
  rulesLoading: false,
  rulesPagination: {
    page: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0
  },
  rulesFilters: {
    team: '',
    enabled: null,
    search: ''
  },

  // Batch processing state
  selectedTeam: '',  // Persisted across tab switches
  batches: [],
  currentBatch: null,
  batchStatus: null,
  batchResults: [],
  batchLoading: false,
  batchHistoryLoading: false,
  uploadProgress: 0,
  batchResultsPagination: {
    page: 0,
    size: 50,
    totalElements: 0,
    totalPages: 0,
    totalCategorizedElements: 0,
    totalValidatedElements: 0,
    totalFailedElements: 0
  },

  // Data items state
  dataItems: [],
  selectedDataItem: null,
  dataItemsLoading: false,
  dataItemsPagination: {
    page: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0
  },

  // Common state
  loading: false,
  error: null,
  successMessage: null,
  pagination: {
    page: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0,
    sortBy: 'id',
    sortDirection: 'desc'
  },
  filters: {
    status: null,
    categoryType: null,
    search: null
  },
  auditLog: []
};

/**
 * Reducer function for data categorization state
 * @param {Object} state - Current state
 * @param {Object} action - Action object
 * @returns {Object} New state
 */
const dataCategoryReducer = (state = initialState, action) => {
  switch (action.type) {
    // ==================== TEAM MANAGEMENT ====================
    
    case actionTypes.ADD_TEAM:
      return {
        ...state,
        teamsLoading: true,
        error: null
      };
    
    case actionTypes.ADD_TEAM_SUCCESS:
      return handleAddTeamSuccess(state, action.payload);
    
    case actionTypes.ADD_TEAM_ERROR:
      return {
        ...state,
        teamsLoading: false,
        error: action.payload
      };
    
    case actionTypes.REMOVE_TEAM:
      return {
        ...state,
        teamsLoading: true,
        error: null
      };
    
    case actionTypes.REMOVE_TEAM_SUCCESS: {
      const removedValue = action.payload;
      const remainingRegistry = removeTeamFromRegistry(state.teamRegistry, removedValue);
      return {
        ...state,
        teams: state.teams.filter(t => t.value !== removedValue),
        teamRegistry: remainingRegistry,
        teamsLoading: false,
        successMessage: 'Team removed successfully',
        error: null
      };
    }

    case actionTypes.UPDATE_TEAM_MEMBERS: {
      const { teamValue, members } = action.payload;
      const newRegistry = {
        ...state.teamRegistry,
        [teamValue]: { ...state.teamRegistry[teamValue], members },
      };
      saveTeamRegistry(newRegistry);
      return { ...state, teamRegistry: newRegistry };
    }
    
    case actionTypes.REMOVE_TEAM_ERROR:
      return {
        ...state,
        teamsLoading: false,
        error: action.payload
      };


    // GET ALL CATEGORIES
    case actionTypes.GET_ALL_DATA_CATEGORIES:
      return {
        ...state,
        loading: true,
        error: null
      };

    case actionTypes.GET_ALL_DATA_CATEGORIES_SUCCESS:
      return {
        ...state,
        loading: false,
        categories: action.payload.content || [],
        pagination: {
          page: action.payload.number || 0,
          size: action.payload.size || 20,
          totalElements: action.payload.totalElements || 0,
          totalPages: action.payload.totalPages || 0,
          sortBy: state.pagination.sortBy,
          sortDirection: state.pagination.sortDirection
        },
        error: null
      };

    case actionTypes.GET_ALL_DATA_CATEGORIES_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload,
        categories: []
      };

    // GET SINGLE CATEGORY
    case actionTypes.GET_DATA_CATEGORY:
      return {
        ...state,
        loading: true,
        error: null
      };

    case actionTypes.GET_DATA_CATEGORY_SUCCESS:
      return {
        ...state,
        loading: false,
        selectedCategory: action.payload,
        error: null
      };

    case actionTypes.GET_DATA_CATEGORY_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // CREATE CATEGORY
    case actionTypes.CREATE_DATA_CATEGORY:
      return {
        ...state,
        loading: true,
        error: null,
        successMessage: null
      };

    case actionTypes.CREATE_DATA_CATEGORY_SUCCESS:
      return {
        ...state,
        loading: false,
        categories: [action.payload, ...state.categories],
        successMessage: 'Category created successfully',
        error: null
      };

    case actionTypes.CREATE_DATA_CATEGORY_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // UPDATE CATEGORY
    case actionTypes.UPDATE_DATA_CATEGORY:
      return {
        ...state,
        loading: true,
        error: null,
        successMessage: null
      };

    case actionTypes.UPDATE_DATA_CATEGORY_SUCCESS:
      return {
        ...state,
        loading: false,
        categories: state.categories.map(cat =>
          cat.id === action.payload.id ? action.payload : cat
        ),
        selectedCategory: action.payload.id === state.selectedCategory?.id ? action.payload : state.selectedCategory,
        successMessage: 'Category updated successfully',
        error: null
      };

    case actionTypes.UPDATE_DATA_CATEGORY_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // DELETE CATEGORY
    case actionTypes.DELETE_DATA_CATEGORY:
      return {
        ...state,
        loading: true,
        error: null,
        successMessage: null
      };

    case actionTypes.DELETE_DATA_CATEGORY_SUCCESS:
      return {
        ...state,
        loading: false,
        categories: state.categories.filter(cat => cat.id !== action.payload),
        selectedCategory: state.selectedCategory?.id === action.payload ? null : state.selectedCategory,
        successMessage: 'Category deleted successfully',
        error: null
      };

    case actionTypes.DELETE_DATA_CATEGORY_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // CHANGE STATUS
    case actionTypes.CHANGE_CATEGORY_STATUS:
      return {
        ...state,
        loading: true,
        error: null
      };

    case actionTypes.CHANGE_CATEGORY_STATUS_SUCCESS:
      return {
        ...state,
        loading: false,
        categories: state.categories.map(cat =>
          cat.id === action.payload.id ? action.payload : cat
        ),
        successMessage: 'Status changed successfully',
        error: null
      };

    case actionTypes.CHANGE_CATEGORY_STATUS_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // SEARCH CATEGORIES
    case actionTypes.SEARCH_CATEGORIES:
      return {
        ...state,
        loading: true,
        error: null
      };

    case actionTypes.SEARCH_CATEGORIES_SUCCESS:
      return {
        ...state,
        loading: false,
        categories: action.payload.content || [],
        pagination: {
          ...state.pagination,
          page: action.payload.number || 0,
          totalElements: action.payload.totalElements || 0,
          totalPages: action.payload.totalPages || 0
        },
        error: null
      };

    case actionTypes.SEARCH_CATEGORIES_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // GET AUDIT LOG
    case actionTypes.GET_AUDIT_LOG:
      return {
        ...state,
        loading: true,
        error: null
      };

    case actionTypes.GET_AUDIT_LOG_SUCCESS:
      return {
        ...state,
        loading: false,
        auditLog: action.payload,
        error: null
      };

    case actionTypes.GET_AUDIT_LOG_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload,
        auditLog: []
      };

    // CLEAR ERROR
    case actionTypes.CLEAR_DATA_CATEGORY_ERROR:
      return {
        ...state,
        error: null
      };

    // CLEAR SUCCESS MESSAGE
    case actionTypes.CLEAR_SUCCESS_MESSAGE:
      return {
        ...state,
        successMessage: null
      };

    // SET PAGINATION
    case actionTypes.SET_PAGINATION:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          ...action.payload
        }
      };

    // SET FILTERS
    case actionTypes.SET_FILTERS:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload
        }
      };

    // RESET FILTERS
    case actionTypes.RESET_FILTERS:
      return {
        ...state,
        filters: initialState.filters,
        pagination: {
          ...state.pagination,
          page: 0
        }
      };

    // ==================== RULES MANAGEMENT ====================

    // GET ALL RULES
    case actionTypes.GET_ALL_RULES:
      return {
        ...state,
        rulesLoading: true,
        error: null
      };

    case actionTypes.GET_ALL_RULES_SUCCESS: {
      const rawRules = action.payload?.content || action.payload || [];
      const apiRules = normalizeRules(rawRules);
      return {
        ...state,
        rulesLoading: false,
        rules: apiRules,
        rulesPagination: {
          page: action.payload?.number || 0,
          size: action.payload?.size || 20,
          totalElements: action.payload?.totalElements || 0,
          totalPages: action.payload?.totalPages || 0
        },
        error: null
      };
    }

    case actionTypes.GET_ALL_RULES_ERROR:
      return {
        ...state,
        rulesLoading: false,
        error: action.payload
      };

    // GET RULE BY ID
    case actionTypes.GET_RULE_BY_ID:
      return {
        ...state,
        rulesLoading: true,
        error: null
      };

    case actionTypes.GET_RULE_BY_ID_SUCCESS:
      return {
        ...state,
        rulesLoading: false,
        selectedRule: action.payload,
        error: null
      };

    case actionTypes.GET_RULE_BY_ID_ERROR:
      return {
        ...state,
        rulesLoading: false,
        error: action.payload
      };

    // CREATE RULE
    case actionTypes.CREATE_RULE:
      return {
        ...state,
        rulesLoading: true,
        error: null,
        successMessage: null
      };

    case actionTypes.CREATE_RULE_SUCCESS:
      return {
        ...state,
        rulesLoading: false,
        rules: [action.payload, ...state.rules],
        successMessage: 'Rule created successfully',
        error: null
      };

    case actionTypes.CREATE_RULE_ERROR:
      return {
        ...state,
        rulesLoading: false,
        error: action.payload
      };

    // UPDATE RULE
    case actionTypes.UPDATE_RULE:
      return {
        ...state,
        rulesLoading: true,
        error: null,
        successMessage: null
      };

    case actionTypes.UPDATE_RULE_SUCCESS:
      return {
        ...state,
        rulesLoading: false,
        rules: state.rules.map(rule =>
          rule.ruleId === action.payload.ruleId ? action.payload : rule
        ),
        selectedRule: action.payload.ruleId === state.selectedRule?.ruleId ? action.payload : state.selectedRule,
        successMessage: 'Rule updated successfully',
        error: null
      };

    case actionTypes.UPDATE_RULE_ERROR:
      return {
        ...state,
        rulesLoading: false,
        error: action.payload
      };

    // DELETE RULE
    case actionTypes.DELETE_RULE:
      return {
        ...state,
        rulesLoading: true,
        error: null,
        successMessage: null
      };

    case actionTypes.DELETE_RULE_SUCCESS:
      return {
        ...state,
        rulesLoading: false,
        rules: state.rules.filter(rule => rule.ruleId !== action.payload),
        selectedRule: state.selectedRule?.ruleId === action.payload ? null : state.selectedRule,
        successMessage: 'Rule deleted successfully',
        error: null
      };

    case actionTypes.DELETE_RULE_ERROR:
      return {
        ...state,
        rulesLoading: false,
        error: action.payload
      };

    // TOGGLE RULE STATUS
    case actionTypes.TOGGLE_RULE_STATUS:
      return {
        ...state,
        rulesLoading: true,
        error: null
      };

    case actionTypes.TOGGLE_RULE_STATUS_SUCCESS: {
      const updatedRule = {
        ...action.payload,
        ruleId: action.payload.ruleId || action.payload.id,
      };
      return {
        ...state,
        rulesLoading: false,
        rules: state.rules.map(rule =>
          rule.ruleId === updatedRule.ruleId ? updatedRule : rule
        ),
        successMessage: 'Rule status updated successfully',
        error: null
      };
    }

    case actionTypes.TOGGLE_RULE_STATUS_ERROR:
      return {
        ...state,
        rulesLoading: false,
        error: action.payload
      };

    // ==================== BATCH PROCESSING ====================

    // PERSIST SELECTED TEAM
    case actionTypes.SET_SELECTED_TEAM:
      return {
        ...state,
        selectedTeam: action.payload || ''
      };

    // UPLOAD BATCH FILE
    case actionTypes.UPLOAD_BATCH_FILE:
      return {
        ...state,
        batchLoading: true,
        uploadProgress: 0,
        error: null,
        successMessage: null
      };

    case actionTypes.UPLOAD_BATCH_FILE_PROGRESS:
      return {
        ...state,
        uploadProgress: action.payload
      };

    case actionTypes.UPLOAD_BATCH_FILE_SUCCESS:
      return {
        ...state,
        batchLoading: false,
        uploadProgress: 100,
        currentBatch: action.payload,
        batches: [action.payload, ...state.batches],
        successMessage: 'Batch file uploaded successfully',
        error: null
      };

    case actionTypes.UPLOAD_BATCH_FILE_ERROR:
      return {
        ...state,
        batchLoading: false,
        uploadProgress: 0,
        error: action.payload
      };

    // GET BATCH STATUS
    case actionTypes.GET_BATCH_STATUS:
      return {
        ...state,
        batchLoading: true,
        error: null
      };

    case actionTypes.GET_BATCH_STATUS_SUCCESS:
      return {
        ...state,
        batchLoading: false,
        batchStatus: action.payload,
        currentBatch: action.payload,
        error: null
      };

    case actionTypes.GET_BATCH_STATUS_ERROR:
      return {
        ...state,
        batchLoading: false,
        error: action.payload
      };

    // CANCEL BATCH
    case actionTypes.CANCEL_BATCH:
      return {
        ...state,
        batchLoading: true,
        error: null
      };

    case actionTypes.CANCEL_BATCH_SUCCESS:
      return {
        ...state,
        batchLoading: false,
        batchStatus: action.payload,
        successMessage: 'Batch processing cancelled',
        error: null
      };

    case actionTypes.CANCEL_BATCH_ERROR:
      return {
        ...state,
        batchLoading: false,
        error: action.payload
      };

    // GET BATCH RESULTS
    case actionTypes.GET_BATCH_RESULTS:
      return {
        ...state,
        batchLoading: true,
        error: null
      };

    case actionTypes.GET_BATCH_RESULTS_SUCCESS: {
      const rawResults = action.payload?.content || action.payload || [];
      const transformedResults = rawResults.map(transformBatchResult);

      return {
        ...state,
        batchLoading: false,
        batchResults: transformedResults,
        batchResultsPagination: {
          page: action.payload?.number || 0,
          size: action.payload?.size || 50,
          totalElements: action.payload?.totalElements || 0,
          totalPages: action.payload?.totalPages || 0,
          totalCategorizedElements: action.payload?.totalCategorizedElements || 0,
          totalValidatedElements: action.payload?.totalValidatedElements || 0,
          totalFailedElements: action.payload?.totalFailedElements || 0
        },
        error: null
      };
    }

    case actionTypes.GET_BATCH_RESULTS_ERROR:
      return {
        ...state,
        batchLoading: false,
        error: action.payload,
        batchResults: []
      };

    // GET RECENT BATCHES
    case actionTypes.GET_RECENT_BATCHES:
      return {
        ...state,
        batchHistoryLoading: true,
        error: null
      };

    case actionTypes.GET_RECENT_BATCHES_SUCCESS:
      return {
        ...state,
        batchHistoryLoading: false,
        batches: action.payload || [],
        error: null
      };

    case actionTypes.GET_RECENT_BATCHES_ERROR:
      return {
        ...state,
        batchHistoryLoading: false,
        error: null // Don't show error for failed batch history - it's not critical
      };

    // REPROCESS BATCH
    case actionTypes.REPROCESS_BATCH:
      return {
        ...state,
        batchLoading: true,
        error: null
      };

    case actionTypes.REPROCESS_BATCH_SUCCESS:
      return {
        ...state,
        batchLoading: false,
        batchStatus: action.payload,
        successMessage: `Batch re-processed successfully. ${action.payload?.successCount || 0} succeeded, ${action.payload?.failedCount || 0} uncategorized.`,
        error: null
      };

    case actionTypes.REPROCESS_BATCH_ERROR:
      return {
        ...state,
        batchLoading: false,
        error: action.payload
      };

    // ==================== RESULT VALIDATION ====================

    // VALIDATE RESULT
    case actionTypes.VALIDATE_RESULT:
      return {
        ...state,
        loading: true,
        error: null
      };

    case actionTypes.VALIDATE_RESULT_SUCCESS:
      return {
        ...state,
        loading: false,
        batchResults: state.batchResults.map(result =>
          result.resultId === action.payload.resultId ? action.payload : result
        ),
        successMessage: 'Result validated successfully',
        error: null
      };

    case actionTypes.VALIDATE_RESULT_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // BULK VALIDATE RESULTS
    case actionTypes.BULK_VALIDATE_RESULTS:
      return {
        ...state,
        loading: true,
        error: null
      };

    case actionTypes.BULK_VALIDATE_RESULTS_SUCCESS:
      return {
        ...state,
        loading: false,
        successMessage: `${action.payload.count} results validated successfully`,
        error: null
      };

    case actionTypes.BULK_VALIDATE_RESULTS_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // ==================== EXCEL OPERATIONS ====================

    // INGEST EXCEL FILE
    case actionTypes.INGEST_EXCEL_FILE:
      return {
        ...state,
        loading: true,
        error: null
      };

    case actionTypes.INGEST_EXCEL_FILE_SUCCESS:
      return {
        ...state,
        loading: false,
        successMessage: 'Excel file ingested successfully',
        error: null
      };

    case actionTypes.INGEST_EXCEL_FILE_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // EXPORT TO EXCEL
    case actionTypes.EXPORT_TO_EXCEL:
      return {
        ...state,
        loading: true,
        error: null
      };

    case actionTypes.EXPORT_TO_EXCEL_SUCCESS:
      return {
        ...state,
        loading: false,
        successMessage: 'Data exported successfully',
        error: null
      };

    case actionTypes.EXPORT_TO_EXCEL_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // ==================== DATA ITEMS ====================

    // GET ALL DATA ITEMS
    case actionTypes.GET_ALL_DATA_ITEMS:
      return {
        ...state,
        dataItemsLoading: true,
        error: null
      };

    case actionTypes.GET_ALL_DATA_ITEMS_SUCCESS:
      return {
        ...state,
        dataItemsLoading: false,
        dataItems: action.payload?.content || action.payload || [],
        dataItemsPagination: {
          page: action.payload?.number || 0,
          size: action.payload?.size || 20,
          totalElements: action.payload?.totalElements || 0,
          totalPages: action.payload?.totalPages || 0
        },
        error: null
      };

    case actionTypes.GET_ALL_DATA_ITEMS_ERROR:
      return {
        ...state,
        dataItemsLoading: false,
        error: action.payload,
        dataItems: []
      };

    // GET DATA ITEM BY ID
    case actionTypes.GET_DATA_ITEM_BY_ID:
      return {
        ...state,
        dataItemsLoading: true,
        error: null
      };

    case actionTypes.GET_DATA_ITEM_BY_ID_SUCCESS:
      return {
        ...state,
        dataItemsLoading: false,
        selectedDataItem: action.payload,
        error: null
      };

    case actionTypes.GET_DATA_ITEM_BY_ID_ERROR:
      return {
        ...state,
        dataItemsLoading: false,
        error: action.payload
      };

    // CATEGORIZE DATA ITEM
    case actionTypes.CATEGORIZE_DATA_ITEM:
      return {
        ...state,
        dataItemsLoading: true,
        error: null
      };

    case actionTypes.CATEGORIZE_DATA_ITEM_SUCCESS:
      return {
        ...state,
        dataItemsLoading: false,
        successMessage: 'Data item categorized successfully',
        error: null
      };

    case actionTypes.CATEGORIZE_DATA_ITEM_ERROR:
      return {
        ...state,
        dataItemsLoading: false,
        error: action.payload
      };

    // RECATEGORIZE DATA ITEM
    case actionTypes.RECATEGORIZE_DATA_ITEM:
      return {
        ...state,
        dataItemsLoading: true,
        error: null
      };

    case actionTypes.RECATEGORIZE_DATA_ITEM_SUCCESS:
      return {
        ...state,
        dataItemsLoading: false,
        dataItems: state.dataItems.map(item =>
          item.id === action.payload.id ? action.payload : item
        ),
        successMessage: 'Data item recategorized successfully',
        error: null
      };

    case actionTypes.RECATEGORIZE_DATA_ITEM_ERROR:
      return {
        ...state,
        dataItemsLoading: false,
        error: action.payload
      };

    // ==================== TEAMS ====================

    // LOAD TEAMS
    case actionTypes.LOAD_TEAMS:
      return {
        ...state,
        teamsLoading: true,
        error: null
      };

    case actionTypes.LOAD_TEAMS_SUCCESS:
      return {
        ...state,
        teams: action.payload || [],
        teamsLoading: false,
        error: null
      };

    case actionTypes.LOAD_TEAMS_ERROR:
      return {
        ...state,
        teamsLoading: false,
        error: action.payload
      };

    // ==================== TEAM MANAGEMENT (API-backed) ====================

    // GET ALL TEAMS DETAILED
    case actionTypes.GET_ALL_TEAMS_DETAILED:
      return { ...state, teamDetailsLoading: true };

    case actionTypes.GET_ALL_TEAMS_DETAILED_SUCCESS:
      return { ...state, teamDetails: action.payload || [], teamDetailsLoading: false };

    case actionTypes.GET_ALL_TEAMS_DETAILED_ERROR:
      return { ...state, teamDetailsLoading: false, error: action.payload };

    // CREATE TEAM
    case actionTypes.CREATE_TEAM:
      return { ...state, teamsLoading: true, error: null };

    case actionTypes.CREATE_TEAM_SUCCESS:
      return {
        ...state,
        teamsLoading: false,
        teamDetails: [...state.teamDetails, action.payload],
        successMessage: `Team "${action.payload.name}" created successfully`,
        error: null
      };

    case actionTypes.CREATE_TEAM_ERROR:
      return { ...state, teamsLoading: false, error: action.payload };

    // DELETE TEAM
    case actionTypes.DELETE_TEAM:
      return { ...state, teamsLoading: true, error: null };

    case actionTypes.DELETE_TEAM_SUCCESS:
      return {
        ...state,
        teamsLoading: false,
        teamDetails: state.teamDetails.filter(t => t.id !== action.payload.teamId),
        successMessage: 'Team deleted successfully',
        error: null
      };

    case actionTypes.DELETE_TEAM_ERROR:
      return { ...state, teamsLoading: false, error: action.payload };

    // ADD TEAM MEMBER
    case actionTypes.ADD_TEAM_MEMBER:
      return { ...state, teamsLoading: true, error: null };

    case actionTypes.ADD_TEAM_MEMBER_SUCCESS:
      return {
        ...state,
        teamsLoading: false,
        teamDetails: state.teamDetails.map(t =>
          t.id === action.payload.id ? action.payload : t
        ),
        successMessage: 'Member added successfully',
        error: null
      };

    case actionTypes.ADD_TEAM_MEMBER_ERROR:
      return { ...state, teamsLoading: false, error: action.payload };

    // REMOVE TEAM MEMBER
    case actionTypes.REMOVE_TEAM_MEMBER:
      return { ...state, teamsLoading: true, error: null };

    case actionTypes.REMOVE_TEAM_MEMBER_SUCCESS:
      return {
        ...state,
        teamsLoading: false,
        teamDetails: state.teamDetails.map(t =>
          t.id === action.payload.id ? action.payload : t
        ),
        successMessage: 'Member removed successfully',
        error: null
      };

    case actionTypes.REMOVE_TEAM_MEMBER_ERROR:
      return { ...state, teamsLoading: false, error: action.payload };

    // GET MY TEAMS
    case actionTypes.GET_MY_TEAMS:
      return { ...state, myTeamsLoading: true };

    case actionTypes.GET_MY_TEAMS_SUCCESS:
      return { ...state, myTeams: action.payload || [], myTeamsLoading: false };

    case actionTypes.GET_MY_TEAMS_ERROR:
      return { ...state, myTeamsLoading: false, error: action.payload };

    // CHECK TEAM ACCESS
    case actionTypes.CHECK_TEAM_ACCESS_SUCCESS:
      return { ...state, teamAccessResult: action.payload };

    case actionTypes.CHECK_TEAM_ACCESS_ERROR:
      return { ...state, teamAccessResult: null, error: action.payload };

    // ==================== ATTESTATION ====================

    case actionTypes.ATTEST_RESULT:
      return {
        ...state,
        loading: true,
        error: null
      };

    case actionTypes.ATTEST_RESULT_SUCCESS: {
      const attested = action.payload;
      const updatedBatchResults = state.batchResults.map(r => updateResultWithAttestation(r, attested));
      const actionVerb = attested.verdict === 'ACCEPTED' ? 'accepted' : 'denied';
      return {
        ...state,
        loading: false,
        batchResults: updatedBatchResults,
        successMessage: `Result ${actionVerb} successfully`,
        error: null
      };
    }

    case actionTypes.ATTEST_RESULT_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    case actionTypes.BULK_ATTEST_RESULTS:
      return {
        ...state,
        loading: true,
        error: null
      };

    case actionTypes.BULK_ATTEST_RESULTS_SUCCESS: {
      const bulkPayload = action.payload;
      const requests = bulkPayload._requests || [];
      const verdictMap = buildVerdictMap(requests);
      const attestedIds = new Set(Object.keys(verdictMap).map(Number));
      const updatedResults = applyBulkAttestation(state.batchResults, attestedIds, verdictMap);
      
      return {
        ...state,
        loading: false,
        batchResults: updatedResults,
        successMessage: `Bulk attestation completed: ${bulkPayload.accepted || 0} accepted, ${bulkPayload.denied || 0} denied`,
        error: null
      };
    }

    case actionTypes.BULK_ATTEST_RESULTS_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // ACCEPT ALL BATCH (server-side bulk acceptance — all pages, no client-side limit)
    case actionTypes.ACCEPT_ALL_BATCH:
      return {
        ...state,
        acceptingAllBatch: true,
        error: null
      };

    case actionTypes.ACCEPT_ALL_BATCH_SUCCESS: {
      const { batchId, acceptedCount } = action.payload;
      // Mark all CATEGORIZED rows currently in memory as VALIDATED so the grid updates immediately
      const updatedResults = state.batchResults.map(r =>
        r.status === 'CATEGORIZED' ? { ...r, status: 'VALIDATED', isValidated: true } : r
      );
      const countMessage = acceptedCount !== null && acceptedCount !== undefined ? acceptedCount : 'all';
      // Update pagination stats: move categorized count to validated
      const prevPagination = state.batchResultsPagination || {};
      const movedCount = acceptedCount ?? prevPagination.totalCategorizedElements ?? 0;
      const updatedPagination = {
        ...prevPagination,
        totalCategorizedElements: 0,
        totalValidatedElements: (prevPagination.totalValidatedElements || 0) + movedCount
      };
      return {
        ...state,
        acceptingAllBatch: false,
        batchResults: updatedResults,
        batchResultsPagination: updatedPagination,
        successMessage: `Accepted ${countMessage} categorized results for batch ${batchId}`,
        error: null
      };
    }

    case actionTypes.ACCEPT_ALL_BATCH_ERROR:
      return {
        ...state,
        acceptingAllBatch: false,
        error: action.payload
      };

    default:
      return state;
  }
};

export default dataCategoryReducer;

